import { Printer, QrCode } from 'lucide-react';
import { useTenantSettings } from '../../contexts/TenantSettingsContext';
import { buildTenantPrintCss, DEFAULT_BRAND_PRIMARY, BRAND_APP_NAME } from '../../lib/pdf-brand';

/**
 * Composant partagé "Étiquette QR imprimable" (US-EQ-03 / US-EQ-11 + US-BAT-04).
 *
 * Affiche une zone print-friendly (`.print-zone`) avec :
 *  - En-tête : logo + nom du tenant (branding US-TR-06).
 *  - QR pictogramme généré côté client via `api.qrserver.com` (donnée canonique
 *    encodée = `url ?? code`).
 *  - Code + libellé + URL en pied.
 *
 * Le bouton "Imprimer" déclenche `window.print()` et la feuille `@media print`
 * isole automatiquement la zone, en gardant la bande de couleur primaire en
 * bord d'étiquette pour rester reconnaissable.
 */
export interface QrLabelPrintProps {
  /** Code métier affiché en gros sous le QR. */
  code: string;
  /** Libellé affiché en plus petit sous le code. */
  label: string;
  /** URL deeplink encodée dans le QR. Si absent, c'est `code` qui est encodé. */
  url?: string;
  /** Sous-libellé optionnel (ex. numéro d'inventaire pour équipements). */
  sublabel?: string;
}

export function QrLabelPrint({ code, label, url, sublabel }: QrLabelPrintProps) {
  const data = url ?? code;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(
    data,
  )}`;

  const { tenant, settings } = useTenantSettings();
  const tenantName = tenant?.nom ?? BRAND_APP_NAME;
  const logoUrl = tenant?.logo_signed_url ?? tenant?.logo_url ?? null;
  const primary = settings?.branding?.primary_color ?? DEFAULT_BRAND_PRIMARY;

  function handlePrint() {
    window.print();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500 flex items-center">
          <QrCode className="w-3.5 h-3.5 mr-1 text-gray-400" />
          Étiquette imprimable
        </p>
        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 no-print"
        >
          <Printer className="w-3.5 h-3.5 mr-1" />
          Imprimer cette étiquette
        </button>
      </div>

      <div
        className="print-zone flex flex-col items-center p-6 bg-white border border-gray-300 rounded-lg"
        style={{ ['--brand-primary' as string]: primary }}
      >
        <div className="w-full flex items-center justify-between pb-3 mb-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {logoUrl && (
              <img
                src={logoUrl}
                alt=""
                crossOrigin="anonymous"
                className="h-7 w-auto max-w-[120px] object-contain"
              />
            )}
            <span className="text-sm font-semibold text-gray-900">{tenantName}</span>
          </div>
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">
            {BRAND_APP_NAME}
          </span>
        </div>

        <img src={qrSrc} alt="QR code" className="w-48 h-48" />
        <p className="mt-3 text-lg font-bold text-gray-900">{code}</p>
        <p className="text-sm text-gray-700 text-center">{label}</p>
        {sublabel && <p className="text-xs text-gray-500 mt-1">{sublabel}</p>}
        <p className="text-[10px] text-gray-400 mt-2 break-all max-w-xs text-center">{data}</p>
      </div>

      <p className="text-xs text-gray-400 italic no-print">
        Astuce : pour imprimer l'étiquette seule, le navigateur isole automatiquement la zone
        ci-dessus via `@media print`.
      </p>

      <style>{buildTenantPrintCss()}</style>
    </div>
  );
}
